import { Alert, Box, Button, Divider, Grid, Switch, FormControlLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { ContentSection } from 'src/shared/components/organisms/contentSection';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { getEventById, updateEventEmailSettings } from 'src/shared/services/eventService';
import { Event } from '../models/event';

type EventDetailTabEmailSettingsProps = {
  event: Event;
};

type EmailSettingsForm = {
  signupSubject: string;
  signupBody: string;
  paymentSubject: string;
  paymentBody: string;
  ticketSubject: string;
  ticketBody: string;
};

export const EventDetailTabEmailSettings = (props: EventDetailTabEmailSettingsProps) => {
  const { event } = props;
  const { t } = useTranslation(['event', 'common']);

  const [sendOnSignup, setSendOnSignup] = useState(event.signupEmailEnabled ?? false);
  const [resetTarget, setResetTarget] = useState<{ subject: keyof EmailSettingsForm; body: keyof EmailSettingsForm } | null>(null);

  const formMethods = useForm<EmailSettingsForm>({
    mode: 'all',
    defaultValues: {
      signupSubject: event.signupEmailSubject ?? '',
      signupBody: event.signupEmailBody ?? '',
      paymentSubject: event.paymentEmailSubject ?? '',
      paymentBody: event.paymentEmailBody ?? '',
      ticketSubject: event.ticketEmailSubject ?? '',
      ticketBody: event.ticketEmailBody ?? '',
    },
  });

  const {
    handleSubmit,
    formState: { isDirty },
  } = formMethods;

  const onSubmit = async (data: EmailSettingsForm) => {
    try {
      await updateEventEmailSettings(event.id, {
        signupEmailEnabled: sendOnSignup,
        signupEmailSubject: data.signupSubject,
        signupEmailBody: data.signupBody,
        paymentEmailSubject: data.paymentSubject,
        paymentEmailBody: data.paymentBody,
        ticketEmailSubject: data.ticketSubject,
        ticketEmailBody: data.ticketBody,
      });
      toast.success(t('common:success.save') as string);
    } catch {
      toast.error(t('common:error.save') as string);
    }
  };

  const handleResetConfirm = async (result?: { id: string }) => {
    setResetTarget(null);
    if (!result || !resetTarget) return;

    try {
      const currentValues = formMethods.getValues();
      await updateEventEmailSettings(event.id, {
        signupEmailEnabled: sendOnSignup,
        signupEmailSubject: resetTarget.subject === 'signupSubject' ? '' : currentValues.signupSubject,
        signupEmailBody: resetTarget.body === 'signupBody' ? '' : currentValues.signupBody,
        paymentEmailSubject: resetTarget.subject === 'paymentSubject' ? '' : currentValues.paymentSubject,
        paymentEmailBody: resetTarget.body === 'paymentBody' ? '' : currentValues.paymentBody,
        ticketEmailSubject: resetTarget.subject === 'ticketSubject' ? '' : currentValues.ticketSubject,
        ticketEmailBody: resetTarget.body === 'ticketBody' ? '' : currentValues.ticketBody,
      });
      const refreshed = await getEventById(event.id);
      const e = refreshed.data;
      formMethods.reset({
        signupSubject: e.signupEmailSubject ?? '',
        signupBody: e.signupEmailBody ?? '',
        paymentSubject: e.paymentEmailSubject ?? '',
        paymentBody: e.paymentEmailBody ?? '',
        ticketSubject: e.ticketEmailSubject ?? '',
        ticketBody: e.ticketEmailBody ?? '',
      });
      setSendOnSignup(e.signupEmailEnabled ?? false);
      toast.success(t('common:success.save') as string);
    } catch {
      toast.error(t('common:error.save') as string);
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <GridWrapper>
          {/* Bekräftelsemail vid anmälan */}
          <Grid item xs={12}>
            <ContentSection>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant='subtitle1' fontWeight='bold'>
                    {t('event:emailSettings.signupConfirmation')}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {t('event:emailSettings.signupConfirmationDescription')}
                  </Typography>
                  {sendOnSignup && (
                    <Alert severity='info' sx={{ mt: 2 }}>
                      {t('event:emailSettings.availableFields')}
                    </Alert>
                  )}
                </Grid>
                <Grid item xs={12} md={8}>
                  <FormControlLabel
                    control={<Switch checked={sendOnSignup} onChange={(e) => setSendOnSignup(e.target.checked)} />}
                    label={t('event:emailSettings.enableSignupEmail')}
                  />

                  {sendOnSignup && (
                    <Box sx={{ mt: 2 }}>
                      <ControlledTextField
                        name='signupSubject'
                        label={t('event:emailSettings.subject')}
                        size='small'
                        fullWidth
                        sx={{ mt: 1, mb: 2 }}
                      />
                      <ControlledTextEditor
                        name='signupBody'
                        rows={30}
                        noMargin
                        customFields={[
                          { label: 'Förnamn', value: '[firstName]' },
                          { label: 'Efternamn', value: '[lastName]' },
                          { label: 'Evenemangsnamn', value: '[eventName]' },
                          { label: 'Evenemangsdatum', value: '[eventDate]' },
                        ]}
                      />
                      <Typography
                        variant='body2'
                        color='primary'
                        sx={{ mt: 1, cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => setResetTarget({ subject: 'signupSubject', body: 'signupBody' })}
                      >
                        {t('event:emailSettings.resetToDefault')}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </ContentSection>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Mail vid betalning */}
          <Grid item xs={12}>
            <ContentSection>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant='subtitle1' fontWeight='bold'>
                    {t('event:emailSettings.paymentConfirmation')}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {t('event:emailSettings.paymentConfirmationDescription')}
                  </Typography>
                  <Alert severity='info' sx={{ mt: 2 }}>
                    {t('event:emailSettings.availablePaymentFields')}
                  </Alert>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ControlledTextField
                    name='paymentSubject'
                    label={t('event:emailSettings.subject')}
                    size='small'
                    fullWidth
                    sx={{ mt: 1, mb: 2 }}
                    inputProps={{ maxLength: 90 }}
                  />
                  <ControlledTextEditor
                    name='paymentBody'
                    rows={30}
                    noMargin
                    customFields={[
                      { label: 'Evenemangsnamn', value: '[eventName]' },
                      { label: 'Evenemangsdatum', value: '[eventDate]' },
                      { label: 'Belopp', value: '[amount]' },
                      { label: 'Betalningsdatum', value: '[paidDate]' },
                      { label: 'OCR', value: '[ocr]' },
                    ]}
                  />
                  <Typography
                    variant='body2'
                    color='primary'
                    sx={{ mt: 1, cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => setResetTarget({ subject: 'paymentSubject', body: 'paymentBody' })}
                  >
                    {t('event:emailSettings.resetToDefault')}
                  </Typography>
                </Grid>
              </Grid>
            </ContentSection>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Biljettmail */}
          <Grid item xs={12}>
            <ContentSection>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant='subtitle1' fontWeight='bold'>
                    {t('event:emailSettings.ticketEmail')}
                  </Typography>
                  <Typography variant='body2' color='text.secondary'>
                    {t('event:emailSettings.ticketEmailDescription')}
                  </Typography>
                  <Alert severity='info' sx={{ mt: 2 }}>
                    {t('event:emailSettings.availableTicketFields')}
                  </Alert>
                </Grid>
                <Grid item xs={12} md={8}>
                  <ControlledTextField
                    name='ticketSubject'
                    label={t('event:emailSettings.subject')}
                    size='small'
                    fullWidth
                    sx={{ mt: 1, mb: 2 }}
                    inputProps={{ maxLength: 90 }}
                  />
                  <ControlledTextEditor
                    name='ticketBody'
                    rows={30}
                    noMargin
                    customFields={[
                      { label: 'Evenemangsnamn', value: '[eventName]' },
                      { label: 'Evenemangsdatum', value: '[eventDate]' },
                      { label: 'Biljetter', value: '[tickets]' },
                      { label: 'Evenemangslänk', value: '[eventLink]' },
                    ]}
                  />
                  <Typography
                    variant='body2'
                    color='primary'
                    sx={{ mt: 1, cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => setResetTarget({ subject: 'ticketSubject', body: 'ticketBody' })}
                  >
                    {t('event:emailSettings.resetToDefault')}
                  </Typography>
                </Grid>
              </Grid>
            </ContentSection>
          </Grid>

          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant='contained' disableElevation type='submit' disabled={!isDirty && sendOnSignup === (event.signupEmailEnabled ?? false)}>
              {t('common:button.save')}
            </Button>
          </Grid>
        </GridWrapper>
      </form>
      <DeleteConfirmationDialog
        open={resetTarget !== null}
        title={t('event:emailSettings.resetToDefault')}
        modaltext={t('event:emailSettings.resetConfirm')}
        value='reset'
        onClose={handleResetConfirm}
        confirmButtonText={t('event:emailSettings.resetToDefault')}
      />
    </FormProvider>
  );
};
